/*
 * Infinite announcement marquee.
 *
 * Measures one set of announcements, repeats that set until it covers the
 * viewport with one spare, then hands the movement to a single CSS transform
 * animation. Nothing runs per frame in JavaScript: the loop distance is exactly
 * one set width, so the animation restarts seamlessly.
 *
 * Pauses when scrolled out of view or when the tab is hidden, and stays still
 * for visitors who ask for reduced motion.
 */
class AnnouncementMarquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.announcement-marquee__track');
    this.set = this.querySelector('.announcement-marquee__set');
    if (!this.track || !this.set) return;

    this.speed = parseFloat(this.dataset.speed) || 60;
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.visible = true;

    this.measure = this.measure.bind(this);
    this.scheduleMeasure = this.scheduleMeasure.bind(this);
    this.updatePlayState = this.updatePlayState.bind(this);

    this.measure();

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.scheduleMeasure);
      this.resizeObserver.observe(this);
    } else {
      window.addEventListener('resize', this.scheduleMeasure);
    }

    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting;
        this.updatePlayState();
      });
      this.intersectionObserver.observe(this);
    }

    document.addEventListener('visibilitychange', this.updatePlayState);
    this.motionQuery.addEventListener('change', this.scheduleMeasure);

    // Web fonts change the measured width, so re-measure once they land.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(this.scheduleMeasure);
    }
  }

  disconnectedCallback() {
    if (this.frame) cancelAnimationFrame(this.frame);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    else window.removeEventListener('resize', this.scheduleMeasure);
    if (this.intersectionObserver) this.intersectionObserver.disconnect();
    document.removeEventListener('visibilitychange', this.updatePlayState);
    this.motionQuery.removeEventListener('change', this.scheduleMeasure);
  }

  scheduleMeasure() {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(this.measure);
  }

  measure() {
    this.frame = null;

    if (this.motionQuery.matches) {
      this.removeClones();
      this.classList.remove('announcement-marquee--ready');
      this.appliedWidth = null;
      return;
    }

    // Whole pixels: a fractional loop distance makes the text shimmer as it
    // crosses subpixel boundaries on every repeat.
    const setWidth = Math.round(this.set.getBoundingClientRect().width);
    if (setWidth < 1) return;

    // One spare set beyond the visible area keeps the wrap-around covered.
    const copies = Math.max(2, Math.ceil(this.offsetWidth / setWidth) + 1);
    this.fillClones(copies);

    // Rewriting these mid-run restarts the animation, so only touch them on a
    // real change — a resize that leaves the width alone should not stutter.
    if (this.appliedWidth === setWidth) return;
    this.appliedWidth = setWidth;

    this.style.setProperty('--marquee-distance', `${setWidth}px`);
    this.style.setProperty('--marquee-duration', `${setWidth / this.speed}s`);
    this.classList.add('announcement-marquee--ready');
  }

  fillClones(copies) {
    const clones = this.track.querySelectorAll('.announcement-marquee__set--clone');

    for (let i = clones.length; i > copies - 1; i--) {
      clones[i - 1].remove();
    }

    for (let i = clones.length; i < copies - 1; i++) {
      const clone = this.set.cloneNode(true);
      clone.classList.add('announcement-marquee__set--clone');
      clone.setAttribute('aria-hidden', 'true');
      // Clones are decorative repeats: keep them off the tab order.
      clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
      this.track.appendChild(clone);
    }
  }

  removeClones() {
    this.track.querySelectorAll('.announcement-marquee__set--clone').forEach((clone) => clone.remove());
  }

  updatePlayState() {
    this.classList.toggle('is-paused', document.hidden || !this.visible);
  }
}

customElements.define('announcement-marquee', AnnouncementMarquee);
